import { useState } from "react";
import { Check, ChevronDown, Plus, Paintbrush2 } from "lucide-react";

/**
 * Gate 2 · Surface-Based Editing (LOCKED 2026-02).
 *
 * Renders the "Choose a part of your home" panel — driven entirely by the
 * backend `parts_by_area` catalogue. The frontend hard-codes NO area names
 * or part lists (Implementation Rule 2, LOCKED 2026-02).
 *
 * Three chip states (LOCKED):
 *   • Currently Editing → ink border + linen fill + paintbrush glyph
 *   • Coloured          → colour dot + tick
 *   • Not Yet Coloured  → hatched empty dot + muted label
 *
 * Multi-selection is native tap-to-toggle — no mode.
 */
export default function PartsOfYourHomePanel({
  areas,
  activeArea,
  onChangeArea,
  assignments,
  selectedParts,
  onToggleSelected,
}) {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const activeAreaObj = (areas || []).find((a) => a.slug === activeArea) || null;

  return (
    <nav
      className="bg-white rounded-3xl shadow-soft border border-brand-border shrink-0"
      data-testid="parts-panel"
    >
      {/* Working-on selector */}
      <div className="px-5 pt-4 pb-3 border-b border-brand-border">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted mb-1.5">
          Working on
        </div>
        <button
          type="button"
          onClick={() => setSwitcherOpen((v) => !v)}
          data-testid="area-selector"
          className="w-full h-11 px-4 rounded-2xl border border-brand-border bg-white hover:border-brand-teal transition-colors inline-flex items-center justify-between gap-3"
        >
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink">
            <span aria-hidden="true">{activeAreaObj?.icon || ""}</span>
            <span>{activeAreaObj?.display_name || "Choose an area"}</span>
          </span>
          <ChevronDown size={14} className={`text-brand-muted transition-transform ${switcherOpen ? "rotate-180" : ""}`} />
        </button>
        {switcherOpen && (
          <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="area-selector-dropdown">
            {(areas || []).map((a) => (
              <button
                key={a.slug}
                type="button"
                onClick={() => { setSwitcherOpen(false); onChangeArea(a.slug); }}
                data-testid={`area-selector-option-${a.slug}`}
                className={`h-10 px-3 rounded-xl border text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${
                  a.slug === activeArea
                    ? "border-brand-teal bg-brand-linen text-brand-ink"
                    : "border-brand-border bg-white text-brand-ink hover:border-brand-teal"
                }`}
              >
                <span aria-hidden="true">{a.icon}</span>
                <span className="truncate">{a.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Parts of the active area */}
      {activeAreaObj && (
        <div className="px-4 py-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-muted mb-3 px-1">
            {activeAreaObj.icon} {activeAreaObj.display_name}
          </div>
          <ul className="flex flex-wrap items-stretch gap-2" data-testid={`expanded-area-${activeArea}`}>
            {(activeAreaObj.parts || []).map((part) => {
              const colour = assignments[part.slug];
              const selected = selectedParts.includes(part.slug);
              return (
                <PartChip
                  key={part.slug}
                  slug={part.slug}
                  label={part.display_name}
                  colour={colour}
                  selected={selected}
                  onClick={() => onToggleSelected(part.slug)}
                />
              );
            })}
          </ul>
        </div>
      )}

      {/* Collapsed area rows (with progress summary) */}
      <div className="px-4 pb-4 space-y-1.5">
        {(areas || []).filter((a) => a.slug !== activeArea).map((a) => (
          <CollapsedAreaRow
            key={a.slug}
            area={a}
            assignments={assignments}
            onClick={() => onChangeArea(a.slug)}
          />
        ))}
      </div>

      {/* Growth valve — placeholder until Phase 3 delivers the picker */}
      <div className="px-4 pb-4">
        <button
          type="button"
          data-testid="something-else-btn"
          className="w-full h-11 inline-flex items-center justify-center gap-1.5 px-4 rounded-2xl border border-dashed border-brand-border text-xs font-semibold text-brand-muted hover:text-brand-teal hover:border-brand-teal transition-colors"
          onClick={() => {
            // eslint-disable-next-line no-alert
            alert("More parts of your home are coming soon.");
          }}
        >
          <Plus size={12} /> Something else in my home
        </button>
      </div>
    </nav>
  );
}

function PartChip({ slug, label, colour, selected, onClick }) {
  const coloured = !!colour;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={`part-chip-${slug}`}
        className={`group min-h-[68px] flex items-center gap-3 px-3.5 py-2.5 rounded-2xl border transition-all ${
          selected
            ? "border-brand-ink bg-brand-linen shadow-soft"
            : "border-brand-border bg-white hover:border-brand-ink/40 hover:shadow-soft"
        }`}
      >
        {selected && (
          <Paintbrush2 size={14} className="text-brand-teal shrink-0" />
        )}
        <div className="relative">
          <div
            className="w-8 h-8 rounded-full border-2 border-white shadow-soft"
            style={{ background: colour?.color_hex || "repeating-linear-gradient(45deg,#e7e3d8,#e7e3d8 4px,#f5f1e8 4px,#f5f1e8 8px)" }}
          />
          {coloured && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-brand-teal text-white inline-flex items-center justify-center border-2 border-white">
              <Check size={9} strokeWidth={3} />
            </span>
          )}
        </div>
        <div className="text-left">
          <div className={`text-[12px] font-bold leading-tight ${selected ? "text-brand-ink" : "text-brand-ink"}`}>
            {label}
          </div>
          <div className="text-[10px] text-brand-muted leading-tight">
            {coloured ? (colour?.color_name || colour?.color_hex) : "Not picked"}
          </div>
        </div>
      </button>
    </li>
  );
}

function CollapsedAreaRow({ area, assignments, onClick }) {
  const parts = area.parts || [];
  const total = parts.length;
  const coloured = parts.filter((p) => assignments[p.slug]).length;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`collapsed-area-header-${area.slug}`}
      className="w-full h-12 px-3.5 rounded-2xl border border-brand-border bg-white hover:border-brand-teal hover:bg-brand-linen/20 transition-colors inline-flex items-center gap-3"
    >
      <span aria-hidden="true" className="text-lg">{area.icon}</span>
      <span className="text-sm font-semibold text-brand-ink">{area.display_name}</span>
      <div className="ml-auto inline-flex items-center gap-2">
        <div className="inline-flex items-center gap-1">
          {parts.map((p) => {
            const c = assignments[p.slug];
            return c ? (
              <span
                key={p.slug}
                className="inline-block w-3 h-3 rounded-full border border-white shadow-sm"
                style={{ background: c.color_hex }}
              />
            ) : (
              <span
                key={p.slug}
                className="inline-block w-3 h-3 rounded-full"
                style={{ background: "repeating-linear-gradient(45deg,#e7e3d8,#e7e3d8 2px,#f5f1e8 2px,#f5f1e8 4px)" }}
              />
            );
          })}
        </div>
        <span className="text-[10px] text-brand-muted whitespace-nowrap">{coloured} of {total}</span>
      </div>
    </button>
  );
}
